/* Copyright (C) 2019 SmartboxTv S.A. - All rights reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Erasmo Marín <emarin@smartboxtv.com>
 */

import { observable, action, computed, flow, toJS } from 'mobx';
import has from 'lodash/has';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import forEach from 'lodash/forEach';
import without from 'lodash/without';
import isArray from 'lodash/isArray';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import size from 'lodash/size';
import appStore from './appStore';
import profileStore from './profileStore';
import tasksStore from './tasksStore';
import ErrorsStore from './errorsStore';
import fetch from '../fetch';
import { generateCreate, generateReplace } from './actionCreator';
import _ from '../translations';
import templatesStore from './templatesStore';
import { palettes } from '../pages/appBuilder/sections/layout';

class SiteMapStore {
    constructor() {
        this.baseUrl = '/api/core/sitemaps';
    }

    @observable
    sitemap = {};
    @observable
    generatingDefaultSitemap = false;

    @action
    reset = () => {
        this.sitemap = {};
        this.generatingDefaultSitemap = false;
    };

    cleanSiteMap = sitemap => {
        let clean = cloneDeep(sitemap);

        if (!clean.data) return clean;

        forEach(clean.data, (page, key) => {
            if (!page._id) {
                delete clean.data[key];
            } else if (clean.data[key].subPages) {
                let subpages = clean.data[key].subPages;
                forEach(subpages, (page, key) => {
                    if (!page._id) {
                        delete subpages[key];
                    }
                });
            }
        });
        return clean;
    };

    @action.bound
    getSitemap = flow(function* () {
        try {
            const list = yield fetch.get({
                url: this.baseUrl,
                params: {
                    itemsPerPage: 100,
                },
            });
            if (!appStore.app || !appStore.app._id) yield appStore.getApp();
            let sitemap = find(get(list, 'items', []), (item) => get(item, '__belong.App') === appStore.app._id)
            if (!sitemap && list && !list.error) {
                this.createDefaultSitemap();
                return;
            }

            if (list && list.error && !list.items) {
                ErrorsStore.addError(list, _('Could not get Sitemap'));
            } else {
                //remove wrong pages
                sitemap = this.cleanSiteMap(sitemap);
                this.sitemap = sitemap;
            }
        } catch (e) {
            ErrorsStore.addError(e, _('Could not get Sitemap'));
            console.warn(e);
        }
    });

    @action.bound
    setColorPalette(colorPalette) {
        this.sitemap = { ...toJS(this.sitemap), colorPalette };
    }

    @action.bound
    setTemplate(Template) {
        this.sitemap = { ...toJS(this.sitemap), Template };
    }

    @action.bound
    setSiteMapTree(siteMap) {
        this.sitemap = { ...toJS(this.sitemap), data: siteMap };
    }

    @action.bound
    setHasLogin(hasLogin = false) {
        this.sitemap = { ...toJS(this.sitemap), hasLogin: !!hasLogin };
    }

    @action.bound
    setHasPasswordFeatures(hasPasswordFeatures = false) {
        this.sitemap = { ...toJS(this.sitemap), hasPasswordFeatures: !!hasPasswordFeatures };
    }

    @action.bound
    setHasYouboraActive(hasYouboraActive = false) {
        const youbora = { ...toJS(this.sitemap.youbora) }
        youbora.active = hasYouboraActive;
        this.sitemap = { ...toJS(this.sitemap), youbora };
    }

    @action.bound
    setYouboraId(youbora = {}) {
        this.sitemap = { ...toJS(this.sitemap), youbora };
    }

    @action.bound
    setHasAppsRegister(hasAppsRegister = false) {
        this.sitemap = { ...toJS(this.sitemap), hasAppsRegister: !!hasAppsRegister };
    }

    @action.bound
    setCustomFields(customFields = {}) {
        this.sitemap = { ...toJS(this.sitemap), registerScene: customFields };
    }

    @action.bound
    setHasCustomFooter(hasCustomFooter = false) {
        this.sitemap = { ...toJS(this.sitemap), hasCustomFooter: !!hasCustomFooter };
    }

    @action.bound
    setHasLanding(hasLanding = false) {
        this.sitemap = { ...toJS(this.sitemap), hasLanding: !!hasLanding };
    }

    @action.bound
    setHasSimilarPlaylists(hasSimilarPlaylists = true) {
        let contentScene = { ...toJS(this.sitemap.contentScene) }
        contentScene.hasSimilarPlaylists = hasSimilarPlaylists;
        this.sitemap = { ...toJS(this.sitemap), contentScene };
    }

    @action.bound
    setHasContinuousWatching(hasContinuousWatching = false) {
        this.sitemap = { ...toJS(this.sitemap), hasContinuousWatching: !!hasContinuousWatching };
    }

    @action.bound
    setLayoutComponents(components = 'basic') {
        let sitemapCopy = { ...toJS(this.sitemap) }
        sitemapCopy = {
            ...sitemapCopy,
            layoutComponents: {
                ...sitemapCopy.layoutComponents,
                type: components
            }
        };
        this.sitemap = { ...toJS(this.sitemap), ...sitemapCopy };
    }

    @action.bound
    setCustomFooter(customFooter = {}) {
        this.sitemap = { ...toJS(this.sitemap), customFooter };
    }

    @action.bound
    setSocialSharing(socialSharing = false) {
        this.sitemap = { ...toJS(this.sitemap), socialSharing: !!socialSharing };
    }

    @action.bound
    setSocialSharingLink(socialSharingLink = '') {
        if (socialSharingLink.length === 0) socialSharingLink = undefined;
        this.sitemap = { ...toJS(this.sitemap), socialSharingLink };
    }

    @action.bound
    setRedirections(redirections) {
        const sitemap = { ...toJS(this.sitemap) };
        this.sitemap = {
            ...sitemap,
            redirections: {
                ...sitemap.redirections,
                ...redirections
            }
        };
    };

    @action.bound
    setLoginScene(loginScene) {
        this.sitemap = { ...toJS(this.sitemap), loginScene }
    };

    @action.bound
    setMultiLanguageComponents(values) {
        const sitemap = { ...toJS(this.sitemap) };
        this.sitemap = {
            ...sitemap,
            layoutComponents: {
                ...sitemap.layoutComponents,
                multiLanguageComponents: values
            }
        }
    };

    @action.bound
    saveSiteMap = flow(function* () {
        let item = toJS(this.sitemap);
        if (item.__v) delete item.__v;
        const res = yield this.updateSitemap({ item });
        tasksStore.markAsDone(2);
        return res;
    });

    @action.bound
    updateSitemap = flow(function* ({ items, item = {} } = {}) {
        if (item.__v) delete item.__v;

        return yield generateReplace({
            items,
            item,
            url: `${this.baseUrl}/multiple`,
            store: this,
            customError: _('There was a problem updating sitemap'),
        })();
    });

    @action.bound
    createSitemap = flow(function* ({ item = {} } = {}) {
        if (item.__v) delete item.__v;

        return yield generateCreate({
            item,
            url: this.baseUrl,
            store: this,
            customError: _('There was a problem creating the sitemap'),
        })();
    });

    @action.bound
    createDefaultSitemap = flow(function* () {
        yield templatesStore.listAll();
        this.generatingDefaultSitemap = true;

        if (!appStore.app || !appStore.app._id) yield appStore.getApp();

        const defaultSitemap = {
            __belong: {
                Client: profileStore.client._id,
                App: appStore.app._id,
            },
            data: {
                Home: {
                    isEditing: false,
                    playlists: [],
                    isNewPage: false,
                    title: 'Home',
                    _id: 'Home',
                    isHomePage: true,
                    order: 0,
                },
            },
            Template: templatesStore.templates[0],
            colorPalette: palettes[0],
        };
        yield this.createSitemap({ item: defaultSitemap });
        setTimeout(async () => {
            await this.getSitemap();
            this.generatingDefaultSitemap = false;
        }, 5000);
    });

    @action.bound
    removePlaylist(section, subsection, playlistId) {
        let siteMap = toJS(this.sitemap.data);
        section = toJS(section);
        subsection = toJS(subsection);
        if (subsection) {
            if (has(siteMap, `[${section._id}].subPages[${subsection._id}]`)) {
                let subref = siteMap[section._id].subPages[subsection._id];
                let playlist = find(subref.playlists, { _id: playlistId });
                if (!playlist) return;
                subref.playlists = without(subref.playlists, playlist);
            }
        } else {
            let subref = siteMap[section._id];
            let playlist = find(subref.playlists, { _id: playlistId });
            if (!playlist) return;
            subref.playlists = without(subref.playlists, playlist);
        }
        this.sitemap.data = siteMap;
    }

    /*Add or replace a playlist*/
    @action.bound
    addPlaylist(section, subsection, playlist) {
        let siteMap = toJS(this.sitemap.data);

        if (subsection) {
            if (has(siteMap, `[${section._id}].subPages[${subsection._id}]`)) {
                let playlists = siteMap[section._id].subPages[subsection._id].playlists;
                if (!isArray(playlists)) {
                    siteMap[section._id].subPages[subsection._id].playlists = [playlist];
                } else {
                    let playlistIndexFound = findIndex(playlists, { _id: playlist._id });
                    if (playlistIndexFound === -1)
                        playlists.push(playlist);
                    else
                        siteMap[section._id].subPages[subsection._id].playlists[playlistIndexFound] = playlist;
                }
            }
        } else {
            if (has(siteMap, `[${section._id}]`)) {
                let playlists = siteMap[section._id].playlists;
                if (!isArray(playlists)) {
                    siteMap[section._id].playlists = [playlist];
                } else {
                    let playlistIndexFound = findIndex(playlists, { _id: playlist._id });
                    if (playlistIndexFound === -1) {
                        playlists.push(playlist);
                        siteMap[section._id].playlists = playlists;
                    } else
                        siteMap[section._id].playlists[playlistIndexFound] = playlist;
                }
            }
        }
        this.sitemap.data = siteMap;
    }

    @action.bound
    changePlaylistsOrder(section, subsection, playlists) {
        let siteMap = toJS(this.sitemap.data);
        if (subsection) {
            if (has(siteMap, `[${section._id}].subPages[${subsection._id}]`)) {
                let result = [];
                forEach(playlists, id => {
                    const found = find(siteMap[section._id].subPages[subsection._id].playlists, { _id: id });
                    if (found) result.push(found);
                });
                siteMap[section._id].subPages[subsection._id].playlists = result;
            }
        } else {
            if (has(siteMap, `[${section._id}]`)) {
                let result = [];
                forEach(playlists, id => {
                    const found = find(siteMap[section._id].playlists, { _id: id });
                    if (found) result.push(found);
                });
                siteMap[section._id].playlists = result;
            }
        }
        this.sitemap.data = siteMap;
    }

    @computed
    get sectionsForSelect() {
        if (!this.sitemap) return [];
        let sections = [];
        forEach(this.sitemap.data, (page, key) => {
            if (size(page.subPages) === 0) {
                sections.push({
                    text: _(page.title),
                    value: key,
                });
            }
            if (page.subPages) {
                forEach(page.subPages, (subpage, key) => {
                    sections.push({
                        text: `${_(page.title)} - ${_(subpage.title)}`,
                        value: key,
                    });
                });
            }
        });
        return sections;
    }
}

export default new SiteMapStore();
